import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { Checklist } from "@/components/content/Checklist";
import { DIYvsDoneForYou } from "@/components/content/DIYvsDoneForYou";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Search, FileText, MapPin, Gauge, Image, Star } from "lucide-react";

const faqs = [
  {
    question: "How is landscaping SEO different from lawn care SEO?",
    answer: "Landscaping SEO emphasizes visual content (portfolios, galleries), project-based keywords, and often targets higher-value searches. Image optimization and portfolio pages matter more."
  },
  {
    question: "Should I focus on design or installation keywords?",
    answer: "Both! Target 'landscape design [city]' for leads in the planning phase and 'patio installation [city]' for ready-to-buy customers. Create separate pages for each."
  },
  {
    question: "How important are project photos for landscaping SEO?",
    answer: "Very important. Optimize images with descriptive file names and alt text. Create project galleries. Google Images can drive significant traffic for landscaping."
  },
  {
    question: "Should I list on Houzz for SEO?",
    answer: "Yes! Houzz is a valuable platform for landscapers. It provides a backlink, helps with credibility, and reaches homeowners actively planning outdoor projects."
  }
];

const LandscapingWebsiteSEOChecklist = () => {
  const pageUrl = "https://www.leafrushmarketing.com/website-templates/landscaping-website-seo-checklist/";
  
  return (
    <>
      <SEO
        title="Landscaping Website SEO Checklist | Rank Higher on Google"
        description="Complete SEO checklist for landscaping websites. On-page, technical, local, and image SEO to rank for landscaping searches."
        url={pageUrl}
        keywords="landscaping SEO checklist, landscaping website SEO, local SEO landscapers"
      />
      <PageSchema
        pageUrl={pageUrl}
        pageName="Landscaping Website SEO Checklist"
        pageDescription="Complete SEO checklist for landscaping company websites."
        faqs={faqs}
      />

      <TopCTA />

      <section className="py-16 md:py-24">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge variant="secondary">SEO Checklist</Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Landscaping Website{" "}
              <span className="text-primary">SEO Checklist</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Complete SEO checklist for landscaping companies. Rank higher for 
              landscaping, hardscaping, and outdoor living searches.
            </p>
            <LastUpdated />
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto space-y-8">
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  On-Page SEO Checklist
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Checklist
                  items={[
                    "Unique title tag for each page (under 60 characters)",
                    "Meta description for each page (under 160 characters)",
                    "One H1 tag per page with main keyword + city",
                    "H2-H3 subheadings with service keywords",
                    "Service-specific keywords (hardscape, patio, retaining wall)",
                    "City and neighborhood mentions throughout",
                    "Clear CTAs: 'Schedule Consultation' or 'Get Project Estimate'",
                    "Internal links between services and portfolio",
                    "Phone number and contact info visible"
                  ]}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="h-5 w-5 text-primary" />
                  Image & Portfolio SEO
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Checklist
                  items={[
                    "Descriptive file names (patio-installation-austin-tx.jpg)",
                    "Alt text on all images with keywords",
                    "Images compressed for fast loading",
                    "Before/after comparisons with labels",
                    "Project galleries organized by service type",
                    "Caption text with project details",
                    "High-quality images (but optimized file size)",
                    "Portfolio page with filtering options",
                    "Individual project pages for major projects"
                  ]}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gauge className="h-5 w-5 text-primary" />
                  Technical SEO Checklist
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Checklist
                  items={[
                    "Mobile-friendly and responsive design",
                    "Page speed under 3 seconds (critical with image-heavy sites)",
                    "SSL certificate (https://)",
                    "XML sitemap with image sitemap",
                    "No broken links or 404 errors",
                    "Lazy loading for gallery images",
                    "Proper image dimensions specified",
                    "Google Search Console verified",
                    "Schema markup (LocalBusiness, ImageGallery)",
                    "Core Web Vitals passing"
                  ]}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  Local SEO Checklist
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Checklist
                  items={[
                    "Google Business Profile fully optimized",
                    "Photos uploaded to GBP regularly",
                    "Services listed on GBP",
                    "Service areas defined correctly",
                    "NAP consistent across all directories",
                    "Listed on Houzz with portfolio",
                    "Listed on Angi/HomeAdvisor",
                    "Chamber of Commerce listing",
                    "Industry association memberships",
                    "Local supplier/partner backlinks"
                  ]}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="h-5 w-5 text-primary" />
                  Content & Pages Checklist
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Checklist
                  items={[
                    "Dedicated page for each service (design, patios, walls, etc.)",
                    "Service area pages for cities you serve",
                    "Portfolio/gallery page with project photos",
                    "Individual project case studies (for major projects)",
                    "About page with team and company story",
                    "Process page explaining how you work",
                    "FAQ page or FAQs on service pages",
                    "Blog with seasonal and educational content",
                    "Testimonials page with photos when possible"
                  ]}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-primary" />
                  Reviews & Reputation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Checklist
                  items={[
                    "Active review generation strategy",
                    "Reviews on Google, Houzz, and Angi",
                    "Responding to all reviews promptly",
                    "Reviews displayed on website",
                    "Video testimonials when possible",
                    "Case studies with customer quotes",
                    "Before/after with customer permission",
                    "Referral program for happy customers"
                  ]}
                />
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      <MidPageCTA />

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">DIY vs. Done-For-You</h2>
            <DIYvsDoneForYou
              diyPoints={[
                "Work through this checklist item by item",
                "Focus on portfolio optimization",
                "Free but requires ongoing effort",
                "Best if you have time and interest in SEO"
              ]}
              doneForYouPoints={[
                "We handle all SEO and portfolio optimization",
                "Image compression and lazy loading setup",
                "Monthly reporting on rankings and traffic",
                "GBP and directory management included"
              ]}
            />
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-2xl mx-auto">
            <InternalLinks
              title="Related Resources"
              links={[
                { to: "/website-templates/landscaping-homepage-template/", label: "Landscaping Homepage Template" },
                { to: "/website-templates/landscaping-service-page-template/", label: "Service Page Template" },
                { to: "/website-templates/service-area-page-template/", label: "Service Area Page Template" },
                { to: "/service-pages-that-rank/", label: "Service Pages That Rank" },
                { to: "/website-templates/", label: "All Templates" }
              ]}
            />
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />
      <BottomCTA />
      <CalendlyEmbed />
    </>
  );
};

export default LandscapingWebsiteSEOChecklist;
